import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Sparkles, Loader2, Calendar, FileText, Video, Image as ImageIcon,
  ArrowRight, Check, ChevronLeft, ChevronRight
} from 'lucide-react';

const ContentStrategy = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [structure, setStructure] = useState(null);
  const [pillars, setPillars] = useState([]);
  const [weekContent, setWeekContent] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [structureRes, historyRes] = await Promise.all([
        axios.get(`${API}/content-strategy/structure`),
        axios.get(`${API}/content-strategy/history`, { headers })
      ]);
      setStructure(structureRes.data.strategy);
      setPillars(structureRes.data.pillars);
      setHistory(historyRes.data.strategies);
      
      // Load latest week if exists
      if (historyRes.data.strategies.length > 0) {
        const latest = historyRes.data.strategies[0];
        setWeekContent(latest);
        setSelectedWeek(latest.week_number);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const generateWeekContent = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/content-strategy/generate-week`, 
        { week_number: selectedWeek },
        { headers }
      );
      setWeekContent(response.data);
      toast.success('Weekly content generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  const getPillarColor = (pillarId) => {
    const pillar = pillars.find(p => p.id === pillarId);
    return pillar?.color || '#D4AF37';
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'post': return <ImageIcon size={18} />;
      case 'carousel': return <FileText size={18} />;
      case 'reel': return <Video size={18} />;
      default: return <ImageIcon size={18} />;
    }
  };

  return (
    <DashboardLayout title="Clierxter Content Strategy">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/20 to-transparent rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
              <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">3 POSTS PER WEEK</span>
            </div>
            
            <h2 className="font-serif text-3xl text-white mb-4">The Clierxter Content Strategy</h2>
            <p className="text-[#A1A1AA] mb-6 max-w-xl font-light">
              A proven framework: 3 strategic posts per week following the Inspire → Educate → Promote pillars.
              Consistent, purposeful content that builds your brand.
            </p>
          </div>
        </motion.div>

        {/* Weekly Structure */}
        {structure && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-8 mb-8">
            <h3 className="font-serif text-xl text-white mb-6">Weekly Schedule</h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              {structure.schedule.map((day, index) => {
                const pillar = pillars.find(p => p.id === day.pillar);
                return (
                  <div key={index} className="p-6 bg-[#0A0A0A] border border-[#27272A]">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[#E5C585] font-serif text-lg">{day.day}</span>
                      <div className="w-3 h-3" style={{ backgroundColor: pillar?.color }} />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      {getContentIcon(day.content_type)}
                      <span className="text-white text-sm capitalize">{day.content_type}</span>
                    </div>
                    <p className="text-[#D4AF37] text-xs font-accent tracking-wider mb-2">{pillar?.name.toUpperCase()}</p>
                    <p className="text-[#52525B] text-sm">{day.focus}</p>
                  </div>
                );
              })}
            </div>

            {/* Content Pillars */}
            <div className="mt-8 pt-8 border-t border-[#1C1C1C]">
              <h4 className="text-[#A1A1AA] text-sm mb-4">Content Pillars</h4>
              <div className="grid md:grid-cols-3 gap-4">
                {pillars.map(pillar => (
                  <div key={pillar.id} className="flex items-start gap-3">
                    <div className="w-4 h-4 mt-1" style={{ backgroundColor: pillar.color }} />
                    <div>
                      <p className="text-white text-sm font-medium">{pillar.name}</p>
                      <p className="text-[#52525B] text-xs">{pillar.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Week Selector & Generator */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-luxury p-8 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div>
              <h3 className="font-serif text-xl text-white mb-2">Generate Weekly Content</h3>
              <p className="text-[#52525B] text-sm">AI-powered content ideas based on your brand and the Clierxter method.</p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#27272A] p-1">
                <button 
                  onClick={() => setSelectedWeek(Math.max(1, selectedWeek - 1))}
                  className="p-2 text-[#71717A] hover:text-white"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-white px-4">Week {selectedWeek}</span>
                <button 
                  onClick={() => setSelectedWeek(Math.min(4, selectedWeek + 1))}
                  className="p-2 text-[#71717A] hover:text-white"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
              
              <Button onClick={generateWeekContent} disabled={loading} className="btn-gold px-6 py-3" data-testid="generate-week-btn">
                {loading ? (
                  <><Loader2 className="animate-spin mr-2" size={18} />Generating...</>
                ) : (
                  <><Sparkles size={18} className="mr-2" />Generate Week {selectedWeek}</>
                )}
              </Button>
            </div>
          </div>

          {/* Monthly Themes */}
          {structure && (
            <div className="grid grid-cols-4 gap-2">
              {structure.monthly_themes.map((theme, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedWeek(theme.week)}
                  className={`p-4 text-left transition-all ${
                    selectedWeek === theme.week
                      ? 'bg-[#D4AF37]/10 border border-[#D4AF37]'
                      : 'bg-[#0A0A0A] border border-[#27272A] hover:border-[#3F3F46]'
                  }`}
                >
                  <span className="text-[#52525B] text-[10px] font-accent tracking-wider">WEEK {theme.week}</span>
                  <p className={`text-sm mt-1 ${selectedWeek === theme.week ? 'text-[#E5C585]' : 'text-white'}`}>
                    {theme.theme}
                  </p>
                  <p className="text-[#52525B] text-xs mt-1">{theme.focus}</p>
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Generated Content */}
        {weekContent && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
              <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">
                WEEK {weekContent.week_number} • {weekContent.theme?.theme}
              </span>
            </div>

            {weekContent.posts?.map((post, index) => {
              const pillar = pillars.find(p => p.id === post.pillar);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card-premium p-8"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 flex items-center justify-center border border-[#27272A]" style={{ borderColor: pillar?.color }}>
                        {getContentIcon(post.content_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-white font-medium">{post.day}</span>
                          <span className="text-[10px] font-accent tracking-wider px-2 py-0.5" style={{ backgroundColor: `${pillar?.color}20`, color: pillar?.color }}>
                            {pillar?.name.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-[#52525B] text-sm capitalize">{post.content_type}</span>
                      </div>
                    </div>
                  </div>

                  <h4 className="font-serif text-xl text-[#E5C585] mb-4">{post.title}</h4>
                  
                  <div className="space-y-4 text-[#A1A1AA]">
                    <div>
                      <span className="text-[#52525B] text-xs font-accent tracking-wider">HOOK</span>
                      <p className="mt-1">{post.hook}</p>
                    </div>
                    
                    {post.main_message && (
                      <div>
                        <span className="text-[#52525B] text-xs font-accent tracking-wider">MAIN MESSAGE</span>
                        <p className="mt-1">{post.main_message}</p>
                      </div>
                    )}

                    {post.slides && (
                      <div>
                        <span className="text-[#52525B] text-xs font-accent tracking-wider">CAROUSEL SLIDES</span>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          {post.slides.map((slide, i) => (
                            <div key={i} className="p-3 bg-[#0A0A0A] border border-[#27272A] text-sm">
                              <span className="text-[#D4AF37] text-xs">Slide {i + 1}</span>
                              <p className="mt-1 text-white">{slide}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {post.script_outline && (
                      <div>
                        <span className="text-[#52525B] text-xs font-accent tracking-wider">SCRIPT OUTLINE</span>
                        <p className="mt-1">{post.script_outline}</p>
                      </div>
                    )}
                    
                    <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-[#1C1C1C]">
                      <div>
                        <span className="text-[#52525B] text-xs font-accent tracking-wider">CALL TO ACTION</span>
                        <p className="mt-1 text-[#E5C585]">{post.cta}</p>
                      </div>
                      <div>
                        <span className="text-[#52525B] text-xs font-accent tracking-wider">VISUAL DIRECTION</span>
                        <p className="mt-1">{post.visual_direction}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Empty State */}
        {!weekContent && !loading && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-16 text-center">
            <Calendar className="text-[#27272A] mx-auto mb-6" size={80} />
            <h3 className="font-serif text-2xl text-white mb-4">Ready to Plan Your Week?</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto mb-8">
              Select a week above and click generate to create AI-powered content ideas following the Clierxter method.
            </p>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ContentStrategy;
